export const MONTH_NAMES_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export const MONTH_NAMES_FULL = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export interface WeekDateInfo {
  weekNum: number;
  month: string;
  fullMonth: string;
  weekInMonth: number;
  dateRange: string;
  axisLabel: string;
  tooltipTitle: string;
}

/**
 * Calculates ISO-8601 week information for a given year and week number (1..53).
 * Week 1 is the week containing January 4th.
 * A week's assigned month is determined by where its middle day (Thursday) falls.
 */
export function getWeekDateInfo(year: number, weekNum: number): WeekDateInfo {
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const dayOfWeek = jan4.getUTCDay() || 7; // 1 = Mon, 7 = Sun
  const firstMonday = new Date(jan4);
  firstMonday.setUTCDate(jan4.getUTCDate() - dayOfWeek + 1);

  const start = new Date(firstMonday);
  start.setUTCDate(firstMonday.getUTCDate() + (weekNum - 1) * 7);

  const end = new Date(start);
  end.setUTCDate(start.getUTCDate() + 6);

  // Thursday (majority of week days) determines month
  const thursday = new Date(start);
  thursday.setUTCDate(start.getUTCDate() + 3);

  const mIndex = thursday.getUTCMonth();
  const month = MONTH_NAMES_SHORT[mIndex];
  const fullMonth = MONTH_NAMES_FULL[mIndex];

  const sM = MONTH_NAMES_SHORT[start.getUTCMonth()];
  const sD = start.getUTCDate();
  const eM = MONTH_NAMES_SHORT[end.getUTCMonth()];
  const eD = end.getUTCDate();

  const startYear = start.getUTCFullYear();
  const endYear = end.getUTCFullYear();

  let dateRange = "";
  if (startYear !== endYear) {
    dateRange = `${sM} ${sD}, ${startYear} – ${eM} ${eD}, ${endYear}`;
  } else {
    dateRange = `${sM} ${sD} – ${eM} ${eD}, ${year}`;
  }

  return {
    weekNum,
    month,
    fullMonth,
    weekInMonth: 1,
    dateRange,
    axisLabel: `Week ${weekNum} (${month})`,
    tooltipTitle: `Week ${weekNum} (${month}) • ${dateRange}`,
  };
}

/**
 * Pre-computes mapping of all weeks (1..53) for a given year,
 * calculating each week's order within its assigned month (e.g. Feb W1, Feb W2).
 */
export function getYearWeekMap(
  year: number,
  totalWeeks = 53,
): Map<number, WeekDateInfo> {
  const map = new Map<number, WeekDateInfo>();
  let currentMonth = "";
  let weekInMonth = 0;

  for (let w = 1; w <= totalWeeks; w++) {
    const info = getWeekDateInfo(year, w);
    if (info.month !== currentMonth) {
      currentMonth = info.month;
      weekInMonth = 1;
    } else {
      weekInMonth++;
    }

    info.weekInMonth = weekInMonth;
    info.axisLabel = `Week ${w} (${info.month} W${weekInMonth})`;
    info.tooltipTitle = `Week ${w} (${info.month} W${weekInMonth}) • ${info.dateRange}`;

    map.set(w, info);
  }

  return map;
}

/**
 * Transforms raw week labels (e.g. ["Week 1", "Week 2", ...]) into
 * contextual labels containing month and month-week info:
 * e.g. ["Week 1 (Jan W1)", "Week 2 (Jan W2)", ..., "Week 6 (Feb W1)", ...]
 */
export function getWeeklyChartLabels(
  rawLabels: string[],
  year: number,
): string[] {
  const weekMap = getYearWeekMap(year, Math.max(rawLabels.length, 53));
  return rawLabels.map((raw, index) => {
    const match = String(raw).match(/\d+/);
    const weekNum = match ? parseInt(match[0], 10) : index + 1;
    const info = weekMap.get(weekNum);
    return info ? info.axisLabel : raw;
  });
}
